import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import axios from 'axios'
import { userServices, clientServices } from '@/_services'

Vue.use(Vuex)

export default new Vuex.Store({
  plugins: [createPersistedState({ key: 'bonfire-vuex', paths: ['status', 'user_status', 'token', 'refreshToken', 'refreshTokenPromise', 'userProfile', 'userRefresh', 'tokenRefresh'] })],
  state: {
    status: '',
    user_status: '',
    token: '',
    refreshToken: '',
    refreshTokenPromise: null, // Holds the promise of the refresh token
    userProfile: {},
    userRefresh: new Date().getTime(),
    tokenRefresh: new Date().getTime(),
    template: {
      seriesItemChanged: false,
      seriesOrderChanged: false,
      activityChanged: false
    },
    editor: {
      activity: {},
      seriesItem: {}
    }
  },
  mutations: {
    auth_request (state) {
      state.status = 'loading'
    },
    auth_refresh (state) {
      state.status = 'refreshing'
    },
    refreshTokenPromise (state, promise) {
      state.refreshTokenPromise = promise
    },
    // mutations only allow 2 parameters max (state and payload),
    // so we'll deconstruct the second because we are expecting both token and user
    auth_success (state, { token, user, refreshToken }) {
      // console.log('User State: ' + user)

      var newDate = new Date()
      newDate.setDate(newDate.getDate() + 1)

      state.status = 'success'
      state.token = token
      state.refreshToken = refreshToken
      state.userProfile = user
      state.userRefresh = newDate
      state.tokenRefresh = newDate
    },
    auth_error (state) {
      state.status = 'error'
    },
    user_refresh (state) {
      state.user_status = 'refreshing'
    },
    user_success (state, user) {
      var newDate = new Date()
      newDate.setDate(newDate.getDate() + 1)

      state.user_status = 'success'
      state.userProfile = user
      state.userRefresh = newDate
    },
    user_error (state) {
      state.user_status = 'error'
    },
    logout (state) {
      state.status = ''
      state.token = ''
      state.refreshToken = ''
      state.userProfile = ''
      state.userRefresh = new Date().getTime()
      state.tokenRefresh = new Date().getTime()
    },
    activity_changed (state, status) {
      state.template.activityChanged = status.changed
      state.editor.activity = status.activity
    },
    series_item_changed (state, status) {
      console.log('ser1 =>' + status.changed)
      state.template.seriesItemChanged = status.changed
      state.editor.seriesItem = status.seriesItem
    },
    series_order_changed (state, change) {
      state.template.seriesOrderChanged = change
    }
  },
  actions: {
    login ({ commit }, userlogin) {
      return new Promise((resolve, reject) => {
        commit('auth_request')
        commit('refreshTokenPromise', null) // clear any refresh, just in case
        userServices.loginUser(userlogin.email, userlogin.password)
          .then(resp => {
            console.log('Login Token: ' + JSON.stringify(resp.token).slice(0, 50))
            var token = resp.token
            var user = resp.user
            var refreshToken = resp.refreshToken
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
            console.log('User Profile: ' + JSON.stringify(user))
            commit('auth_success', { token, user, refreshToken })
            resolve(resp.user)
          })
          .catch(err => {
            commit('auth_error')
            reject(err)
          })
      })
    },
    register ({ commit }, user) {
      console.log('Store -> register: ' + JSON.stringify(user))
      return new Promise((resolve, reject) => {
        commit('auth_request')
        userServices.registerUser(user)
          .then(resp => {
            console.log('User Registered:' + JSON.stringify(resp))
            console.log('Login Token: ' + JSON.stringify(resp.token).slice(0, 50))
            var token = resp.token
            var user = resp.user
            var refreshToken = resp.refreshToken
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
            console.log('User Profile: ' + JSON.stringify(user))
            commit('auth_success', { token, user, refreshToken })
            resolve(resp.user)
          })
          .catch(err => {
            commit('auth_error')
            reject(err)
          })
      })
    },
    refreshUser ({ commit, state }) {
      console.log('Store -> refresh')
      // https://medium.com/@zitko/structuring-a-vue-project-authentication-87032e5bfe16
      commit('auth_refresh')
      console.log('refresh token promise' + state.refreshTokenPromise)
      if (!state.refreshTokenPromise) {
        console.log('Refresh Promise Init')
        const p = userServices.refreshUser(state.token, state.refreshToken)
        console.log('Refresh Promise working')
        commit('refreshTokenPromise', p)

        p.then(
          async resp => {
            console.log('User Registered:' + JSON.stringify(resp))
            console.log('Login Token: ' + JSON.stringify(resp.token).slice(0, 50))
            var token = resp.token
            var user = resp.user
            var refreshToken = resp.refreshToken
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
            await commit('auth_success', { token, user, refreshToken })
            commit('refreshTokenPromise', null)
          },
          error => {
            commit('refreshTokenPromise', null)
            commit('auth_error')
            console.log('Refresh Token Error: ' + JSON.stringify(error))
          })
      } else {
        console.log('waiting on promise')
      }
      return state.refreshTokenPromise
    },
    logout ({ commit }) {
      // eslint-disable-next-line no-unused-vars
      return new Promise((resolve, reject) => {
        userServices.logoutUser(this.state.refreshToken) // no need to wait
        commit('logout')
        delete axios.defaults.headers.common['Authorization']
        console.log('Signout Complete')
        resolve()
      })
    },
    updateProfile ({ commit }, force) {
      return new Promise((resolve, reject) => {
        if (this.getters.userStatus !== 'refreshing' || force === 'true') {
          commit('user_refresh')
          console.log('Store Dispatch updateProfile')
          userServices.getLocalUser(this.state.userProfile.email)
            .then(data => {
              commit('user_success', data)
            })
            .catch(err => {
              commit('user_error')
              reject(err)
            })
        } else {
          console.log('User Refreshing already working')
        }
      })
    },
    activityChanged ({ commit }, state) {
      // state.changed && state.activity
      console.log('Store.js activityChanged')
      return new Promise((resolve) => {
        commit('activity_changed', state)
        resolve()
      })
    },
    saveActivity ({ commit }) {
      return new Promise((resolve, reject) => {
        console.log('Store.js Saving Activity = ' + JSON.stringify(this.state.editor.activity))
        clientServices.saveActivity(this.state.editor.activity)
          .then(resp => {
            commit('activity_changed', {
              changed: false,
              activity: resp
            })
            console.log('Store.js ActivityDay = ' + JSON.stringify(resp))
            resolve(resp)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    deleteActivity ({ commit }, id) {
      return new Promise((resolve, reject) => {
        console.log('Store.js Deleting Activity = ' + id)
        clientServices.deleteActivity(id)
          .then(resp => {
            commit('activity_changed', {
              changed: false,
              activity: {}
            })
            console.log('Store.js Activity ' + id + ' deleted')
            resolve(true)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    seriesItemChanged ({ commit }, state) {
      // state.changed && state.seriesItem
      return new Promise((resolve) => {
        commit('series_item_changed', state)
        resolve()
      })
    },
    saveSeriesItem ({ commit }) {
      return new Promise((resolve, reject) => {
        console.log('Store.js Series Item = ' + JSON.stringify(this.state.editor.seriesItem))
        clientServices.saveActivityDay(this.state.editor.seriesItem)
          .then(resp => {
            commit('series_item_changed', {
              changed: false,
              activity: resp
            })
            console.log('Store.js SeriesItem = ' + JSON.stringify(resp))
            resolve(resp)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    deleteSeriesItem ({ commit }, id) {
      return new Promise((resolve, reject) => {
        console.log('Store.js Deleting Series Item = ' + id)
        clientServices.deleteActivityDay(id)
          .then(resp => {
            commit('series_item_changed', {
              changed: false,
              seriesItem: {}
            })
            console.log('Store.js Deleted Series Item ' + id)
            resolve(true)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    seriesOrderChanged ({ commit }, state) {
      return new Promise((resolve) => {
        commit('series_order_changed', state)
        resolve()
      })
    },
    clearTemplateChanges ({ commit }) {
      commit('series_item_changed', { changed: false, seriesItem: {} })
      commit('activity_changed', { changed: false, activity: {} })
    }
  },
  getters: {
    isLoggedIn: state => !!state.token,
    authStatus: state => state.status,
    userStatus: state => state.user_status,
    userProfile: state => state.userProfile,
    templateChanged: state => state.template.seriesItemChanged || state.template.seriesOrderChanged || state.template.activityChanged,
    token: state => state.token,
    userExpired: state => {
      var newDate = new Date()
      return state.userProfile && newDate > state.userRefresh
    },
    isOwner: state => state.userProfile && state.userProfile.roles && state.userProfile.roles.includes('Owner'),
    isAdmin: state => state.userProfile && state.userProfile.roles && state.userProfile.roles.includes('Admin')
  }
})
